import { useStable } from '@/hooks/useStable';
import { Device } from '@/types/app';
import { Component } from '@/types/component';
import { MobileAppEnvironment, MobileAppTrackingStatus } from '@/types/mobileApp';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { AppState, AppStateProvider } from 'lib/contexts/AppState';
import { ComponentsStateProvider } from 'lib/contexts/ComponentsState';
import { MobileAppState, MobileAppStateProvider } from 'lib/contexts/MobileAppState';
import getGrowthBook from 'lib/growth-book/getGrowthBook';
import { handleOnFeatureUsage, handleTrackingCallback } from 'lib/growth-book/handleGrowthBookCallback';
import { initGrowthBook } from 'lib/growth-book/initGrowthBook';
import { parseIdFromGaCookie } from 'lib/growth-book/utils';
import useCookies from 'lib/hooks/useCookies';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { Context as GrowthBookContext } from '@growthbook/growthbook/dist/types/growthbook';
import { http } from '@/utils/http';
import getCurrentBrand from '@/utils/getCurrentBrand';

const brand = getCurrentBrand();

const growthBookContext: GrowthBookContext = {
  enableDevMode: true,
  subscribeToChanges: true,
  trackingCallback: handleTrackingCallback,
  onFeatureUsage: handleOnFeatureUsage,
};

export interface StandaloneStateProvidersProps {
  device: Device;
  pageData: any;
  appState?: Partial<AppState>;
  mobileAppState?: Partial<MobileAppState>;
}

export const StandaloneStateProviders: Component<StandaloneStateProvidersProps> = ({ children, ...pageProps }) => {
  const { query, route, pathname, asPath } = useRouter();
  const [growthBookInstance, setGrowthBookInstance] = useState(getGrowthBook({ ...growthBookContext, enabled: false }));

  const { device, pageData, appState, mobileAppState } = useStable(pageProps);

  const { type, data, meta } = pageData ?? {};
  const { id, isNativeAd, accessLevel, pageType: metaPageType } = meta ?? {};
  const { publishedUrl, category, tags } = data ?? {};

  const baseAppState: AppState = useStable({
    device,
    type,
    category,
    publishedUrl,
    tags,
    pageId: id,
    isNativeAd,
    accessLevel,
    metaPageType,
    gamesUrls: ['/korsord', '/korsord-och-spel'],
  });

  const baseMobileAppState: MobileAppState = useStable({
    isMobileApp: Boolean(query.allerapp),
    appVersion: query['app-version'] as string,
    buildVersion: query['build-version'] as string,
    environment: query.environment as MobileAppEnvironment,
    trackingStatus: query['tracking-status'] as MobileAppTrackingStatus,
  });

  const [{ _ga }] = useCookies();
  const prevGaRef = useRef<string | null>(null);

  useEffect(() => {
    if (!_ga) return;

    const currentGaValue = String(_ga);
    const prevGaValue = prevGaRef.current;
    if (prevGaValue && prevGaValue !== currentGaValue && brand?.slug === 'svenskdam') {
      http.post('/api/log', {
        message: `GA Cookie changed. Prev: ${prevGaValue}, Now: ${currentGaValue}`,
        extra: {
          ...baseAppState,
          ...baseMobileAppState,
          route,
          pathname,
          asPath,
          href: window?.location?.href,
        },
        type: 'warn',
      });
    }

    prevGaRef.current = currentGaValue;

    const id = parseIdFromGaCookie(currentGaValue);

    setGrowthBookInstance(getGrowthBook({ ...growthBookContext, enabled: true, attributes: { id } }));
  }, [_ga]);

  useEffect(() => {
    initGrowthBook(growthBookInstance);
  }, [growthBookInstance]);

  return (
    <GrowthBookProvider growthbook={growthBookInstance}>
      <AppStateProvider {...baseAppState} {...appState}>
        <MobileAppStateProvider {...baseMobileAppState} {...mobileAppState}>
          <ComponentsStateProvider>{children}</ComponentsStateProvider>
        </MobileAppStateProvider>
      </AppStateProvider>
    </GrowthBookProvider>
  );
};
